import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import type { DisciplinedInterface } from '../models/disciplined.interface';

export const GetDisciplinedQuery = gql`query getDisciplined($clinicId: Long!) {
  disciplined(clinicId : $clinicId, take: 10000) {
    items {
      name
      id
      clinicId
      clinicName
    }
  }
}`;

@Injectable({
  providedIn: 'root'
})
export class DisciplinedService {
  constructor(private readonly apollo: Apollo) { }

  getDisciplineds(clinicId: number): Observable<DisciplinedInterface[]> {
    return this.apollo.watchQuery<GetDisciplinedResponseInterface>({
      query: GetDisciplinedQuery,
      variables: { clinicId }
    }).valueChanges.pipe(map(res => res.data.disciplined?.items ?? []));
  }

  addOrUpdateDisciplined(label: string, id?: number, clinicId?: number ): Observable<DisciplinedInterface | undefined> {
    return this.apollo.mutate<AddOrUpdateDisciplinedResponseInterface>({
      mutation: gql`mutation addOrUpdateDisciplined($dto: DisciplinedAddOrUpdateDtoInput) {
        addOrUpdateDisciplined(dto: $dto) {
          id
          name
          clinicId
          clinicName
        }
      }`,
      variables: {
        dto: {
          name: label,
          id: id ?? null,
          clinicId: clinicId ?? null
        }
      },
      refetchQueries: [{
        query : GetDisciplinedQuery,
        variables: { clinicId }
      }],
      update: (store, { data }) => {
        const query = store.readQuery<GetDisciplinedResponseInterface>({
          query: GetDisciplinedQuery,
          variables : { clinicId }
        });
        const disciplineds = query?.disciplined?.items?.map(disciplineds=> ({ ...disciplineds }));
        if (data && disciplineds) {
          if (disciplineds.map(k=> k.id).includes(data?.addOrUpdateDisciplined.id)) {
            const disciplined = disciplineds.find(k=> k.id === data?.addOrUpdateDisciplined.id);
            if (disciplined) {
              disciplined.name = data?.addOrUpdateDisciplined.name;
              disciplined.clinicId = data?.addOrUpdateDisciplined.clinicId;
              disciplined.clinicName = data?.addOrUpdateDisciplined.clinicName;
            }
          } else {
            disciplineds.push(data?.addOrUpdateDisciplined);
          }
        }

        disciplineds?.sort((a, b) => a.name.localeCompare(b.name));
        store.writeQuery({
          query: GetDisciplinedQuery,
          variables : { clinicId },
          data: { disciplined : { items: disciplineds } }
        });
      },
    }).pipe(map(res => res.data?.addOrUpdateDisciplined));
  }

  deleteDisciplined(id: number): Observable<DisciplinedInterface | undefined> {
    return this.apollo.mutate<{ deleteDisciplined: DisciplinedInterface }>({
      mutation: gql`mutation deleteDisciplined($id: Long!) {
        deleteDisciplined(id: $id) {
          id
          name
          clinicId
          clinicName
        }
      }`,
      variables: { id }
    }).pipe(map(res => res.data?.deleteDisciplined));
  }
}

export interface GetDisciplinedResponseInterface {
  disciplined: { items: DisciplinedInterface[] };
}

export interface AddOrUpdateDisciplinedResponseInterface {
  addOrUpdateDisciplined: DisciplinedInterface;
}
